import { bindingArgumentsComparison, IdNodeRef, NodeBinding } from '@internal/open-catalog';
import { Box, Chip, createStyles, Grid, IconButton, ListItem, ListItemText, makeStyles, Theme, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { ChangeEvent, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '../../../../../../components/accordion/accordion.styled';
import { MessagingNodeData } from '../flow';
import { BindingDialog } from './dialog.bindings';
import { usePanelEditableStyles } from './panel.style';

export const usePanelBindingStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      height: '100%',
      '& > div:nth-child(odd)': {
        backgroundColor: '#f2f2f2'
      }
    },
    group: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: 0,
      '& > div': {
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5)
      }
    },
    removeButton: {
      textAlign: 'end'
    }
  })
);

export function BindingsPanel(props: {
  data: MessagingNodeData,
  expanded: string | boolean,
  onAccordionStateChange: (panel: any) => (_event: ChangeEvent<{}>, isExpanded: boolean) => void,
  source?: IdNodeRef,
  destination?: IdNodeRef,
  onBindingsChange?: (bindings: NodeBinding[]) => void,
}) {
  
  const classes = { ...usePanelEditableStyles(), ...usePanelBindingStyles() };

  const { data, expanded, source, destination, onAccordionStateChange, onBindingsChange } = props;
  const bindings: NodeBinding[] = (data.spec as any).bindings || [];

  const readonly = !source && !destination && !onBindingsChange;
  const [formDialog, setFormDialog] = useState(false);

  const openBindingFormDialog = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setFormDialog(true);
  }

  const isDuplicatedBinding = (newBinding: NodeBinding): boolean => {

    const found = bindings.filter(b => b.source === newBinding.source)
      .filter(b => b.type === newBinding.type)
      .filter(b => bindingArgumentsComparison(b.arguments, newBinding.arguments));

    return (found || []).length > 0;
  }

  const closeBindingFormDialog = (newBinding?: NodeBinding): void => {

    if (newBinding && !isDuplicatedBinding(newBinding)) {
      bindings.unshift(newBinding);
      if (onBindingsChange) {
        onBindingsChange(bindings);
      }
    }
    setFormDialog(false);
  }

  const removeBinding = (index: number): void => {
    bindings.splice(index, 1);
    if (onBindingsChange) {
      onBindingsChange(bindings);
    }
  };

  return (<>

    {!readonly && source && destination && (
      <BindingDialog open={formDialog} onClose={closeBindingFormDialog} data={data} source={source} destination={destination} />
    )}

    <Accordion expanded={expanded === 'bindings'} onChange={onAccordionStateChange('bindings')}>

      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <ListItem className={classes.headerWithActions}>
          <ListItemText primary="Bindings" />
          {!readonly && (
            <IconButton edge="start" color='secondary' size="small" title="Add binding" onClick={openBindingFormDialog}>
              <AddIcon />
            </IconButton>
          )}
        </ListItem>
      </AccordionSummary>
      <AccordionDetails>

        <Grid container spacing={1} className={classes.root} >

          {!bindings.length && (
            <Typography variant='caption' component="p">
              {readonly ? "No bindings defined." : "No bindings defined, let's add some ! :D"}
            </Typography>
          )}

          {bindings.length > 0 && (
            bindings.map((b, index) => (
              <Grid item xs={12} key={index}>
                <Grid container spacing={1}>
                  <Grid item xs={readonly ? 12 : 11}>
                    <Box className={classes.group}>
                      {Object.keys(b.arguments as any).length > 0 ? (
                        [...Object.entries(b.arguments as any)].map((arg, argIndex) => {
                          const value = arg[1] ? `: ${arg[1]}` : '';
                          return (<Chip key={`${index}-${argIndex}`} size='small' label={`${arg[0]}${value}`} />);
                        })
                      ) : (
                        <Chip size='small' label='All messages' />
                      )}
                    </Box>
                  </Grid>
                  {!readonly && (
                    <Grid item xs={1} className={classes.removeButton}>
                      <IconButton size="small" title="Remove binding" onClick={() => removeBinding(index)}>
                        <DeleteForeverIcon />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            ))
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  </>);
}
